import {
    AlertCircle,
    ArrowLeft,
    Building,
    Calendar,
    CheckCircle,
    Clock,
    DollarSign,
    Edit,
    FileText,
    Mail,
    MapPin,
    Phone,
    User,
    XCircle
} from "lucide-react";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

const RMCContractDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");

  // 계약 상세 정보 조회
  const { data: contractData, isLoading: contractLoading } = useQuery(
    `rmc-contract-${contractId}`,
    async () => {
      const response = await fetch(`/api/rmc/contracts/${contractId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("계약 정보를 불러올 수 없습니다.");
      }

      return response.json();
    }
  );

  // 계약 종료 뮤테이션
  const terminateContractMutation = useMutation(
    async (terminationData) => {
      const response = await fetch(`/api/rmc/contracts/${contractId}/terminate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(terminationData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "계약 종료에 실패했습니다.");
      }

      return response.json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`rmc-contract-${contractId}`);
        queryClient.invalidateQueries("rmc-contracts");
        queryClient.invalidateQueries("rmc-contracts-statistics");
        alert("계약이 성공적으로 종료되었습니다.");
      },
      onError: (error) => {
        alert(`계약 종료 실패: ${error.message}`);
      },
    }
  );

  // 상태별 아이콘과 색상
  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "expired":
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case "terminated":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "활성";
      case "expired":
        return "만료";
      case "terminated":
        return "종료";
      default:
        return "알 수 없음";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "expired":
        return "bg-yellow-100 text-yellow-800";
      case "terminated":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleTerminateContract = () => {
    const terminationDate = prompt("계약 종료일을 입력하세요 (YYYY-MM-DD):");
    const terminationReason = prompt("계약 종료 사유를 입력하세요:");

    if (terminationDate && terminationReason) {
      terminateContractMutation.mutate({
        termination_date: terminationDate,
        termination_reason: terminationReason,
      });
    }
  };

  if (contractLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contractData?.contract) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">계약을 찾을 수 없습니다</h3>
        <p className="text-gray-500">요청하신 계약 정보가 존재하지 않습니다.</p>
      </div>
    );
  }

  const contract = contractData.contract;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/rmc/contracts")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {contract.contract_number}
            </h1>
            <p className="text-gray-600">
              {contract.building?.name} {contract.unit?.unit_number}호
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
              contract.status
            )}`}
          >
            {getStatusIcon(contract.status)}
            <span className="ml-1">{getStatusText(contract.status)}</span>
          </span>
          <button
            onClick={() => navigate(`/rmc/contracts/${contractId}/edit`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>수정</span>
          </button>
          {contract.status === "active" && (
            <button
              onClick={handleTerminateContract}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <XCircle className="w-4 h-4" />
              <span>계약 종료</span>
            </button>
          )}
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "overview", name: "개요", icon: FileText },
            { id: "tenant", name: "임차인 정보", icon: User },
            { id: "property", name: "건물/세대 정보", icon: Building },
            { id: "financial", name: "금액 정보", icon: DollarSign },
            { id: "terms", name: "계약 조건", icon: Calendar },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      <div className="bg-white rounded-lg shadow-sm border">
        {activeTab === "overview" && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">계약 개요</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약번호</label>
                  <p className="text-sm text-gray-900">{contract.contract_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약 유형</label>
                  <p className="text-sm text-gray-900">
                    {contract.contract_type === "monthly" && "월세"}
                    {contract.contract_type === "yearly" && "년세"}
                    {contract.contract_type === "deposit" && "전세"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약 분류</label>
                  <p className="text-sm text-gray-900">{contract.contract_category || "미분류"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약 상태</label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      contract.status
                    )}`}
                  >
                    {getStatusIcon(contract.status)}
                    <span className="ml-1">{getStatusText(contract.status)}</span>
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약 시작일</label>
                  <p className="text-sm text-gray-900">
                    {contract.start_date && new Date(contract.start_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약 종료일</label>
                  <p className="text-sm text-gray-900">
                    {contract.end_date && new Date(contract.end_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">입주일</label>
                  <p className="text-sm text-gray-900">
                    {contract.move_in_date
                      ? new Date(contract.move_in_date).toLocaleDateString()
                      : "미정"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">퇴거일</label>
                  <p className="text-sm text-gray-900">
                    {contract.move_out_date
                      ? new Date(contract.move_out_date).toLocaleDateString()
                      : "미정"}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">갱신일</label>
                  <p className="text-sm text-gray-900">
                    {contract.renewal_date
                      ? new Date(contract.renewal_date).toLocaleDateString()
                      : "미정"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">해지 통보 기간</label>
                  <p className="text-sm text-gray-900">{contract.notice_period}일</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약서 서명일</label>
                  <p className="text-sm text-gray-900">
                    {contract.contract_signed_date
                      ? new Date(contract.contract_signed_date).toLocaleDateString()
                      : "미서명"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약서 검증</label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      contract.contract_verified
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {contract.contract_verified ? "완료" : "미완료"}
                  </span>
                </div>
              </div>
            </div>

            {contract.notes && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-500 mb-2">비고</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{contract.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "tenant" && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">임차인 정보</h2>
            {contract.tenant ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">이름</label>
                    <p className="text-sm text-gray-900">{contract.tenant.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">전화번호</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {contract.tenant.phone}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">이메일</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {contract.tenant.email || "미등록"}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">생년월일</label>
                    <p className="text-sm text-gray-900">
                      {contract.tenant.birth_date
                        ? new Date(contract.tenant.birth_date).toLocaleDateString()
                        : "미등록"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">직업</label>
                    <p className="text-sm text-gray-900">{contract.tenant.occupation || "미등록"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">비상연락처</label>
                    <p className="text-sm text-gray-900">
                      {contract.tenant.emergency_contact || "미등록"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">비상연락처 전화번호</label>
                    <p className="text-sm text-gray-900">
                      {contract.tenant.emergency_phone || "미등록"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">임차인 정보가 등록되지 않았습니다.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "property" && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">건물 및 세대 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 건물 정보 */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  건물 정보
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">건물명</label>
                    <p className="text-sm text-gray-900">{contract.building?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">주소</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {contract.building?.address}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">건물 유형</label>
                    <p className="text-sm text-gray-900">{contract.building?.building_type}</p>
                  </div>
                </div>
              </div>

              {/* 세대 정보 */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">세대 정보</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">세대번호</label>
                    <p className="text-sm text-gray-900">{contract.unit?.unit_number}호</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">층수</label>
                    <p className="text-sm text-gray-900">{contract.unit?.floor}층</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">면적</label>
                    <p className="text-sm text-gray-900">{contract.unit?.area}㎡</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">세대 유형</label>
                    <p className="text-sm text-gray-900">{contract.unit?.unit_type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">기준월세</label>
                    <p className="text-sm text-gray-900">
                      {contract.unit?.standard_monthly_rent ? `${contract.unit.standard_monthly_rent.toLocaleString()}원` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">기준보증금</label>
                    <p className="text-sm text-gray-900">
                      {contract.unit?.standard_deposit ? `${contract.unit.standard_deposit.toLocaleString()}원` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">기준관리비</label>
                    <p className="text-sm text-gray-900">
                      {contract.unit?.standard_maintenance_fee ? `${contract.unit.standard_maintenance_fee.toLocaleString()}원` : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 임대사업자 정보 */}
            <div className="mt-8">
              <h3 className="text-md font-medium text-gray-900 mb-4">임대사업자 정보</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">사업자명</label>
                    <p className="text-sm text-gray-900">{contract.property_owner?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">회사명</label>
                    <p className="text-sm text-gray-900">{contract.property_owner?.company_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">사업자등록번호</label>
                    <p className="text-sm text-gray-900">{contract.property_owner?.business_number}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">이메일</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {contract.property_owner?.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">전화번호</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {contract.property_owner?.phone}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">주소</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {contract.property_owner?.address}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "financial" && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">금액 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">월 임대료</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {contract.monthly_rent?.toLocaleString()}만원
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">보증금</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {contract.deposit?.toLocaleString() || 0}만원
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">관리비</label>
                  <p className="text-sm text-gray-900">
                    {contract.maintenance_fee?.toLocaleString() || 0}원
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">주차비</label>
                  <p className="text-sm text-gray-900">
                    {contract.parking_fee?.toLocaleString() || 0}원
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">납부일</label>
                  <p className="text-sm text-gray-900">매월 {contract.due_date}일</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">연체료율</label>
                  <p className="text-sm text-gray-900">{(contract.late_fee_rate * 100).toFixed(1)}%</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">연체 유예 기간</label>
                  <p className="text-sm text-gray-900">{contract.grace_period}일</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">포함 공과금</label>
                  <p className="text-sm text-gray-900">{contract.utility_included || "없음"}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "terms" && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">계약 조건</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">최대 거주 인원</label>
                  <p className="text-sm text-gray-900">{contract.max_occupants || "제한 없음"}명</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">주차 공간 수</label>
                  <p className="text-sm text-gray-900">{contract.parking_spaces || 0}개</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">반려동물 허용</label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      contract.pet_allowed
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {contract.pet_allowed ? "허용" : "금지"}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">흡연 허용</label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      contract.smoking_allowed
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {contract.smoking_allowed ? "허용" : "금지"}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">계약서 파일</label>
                  <p className="text-sm text-gray-900">
                    {contract.contract_file_path ? (
                      <a
                        href={contract.contract_file_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        계약서 보기
                      </a>
                    ) : (
                      "파일 없음"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {contract.termination_date && (
              <div className="mt-8 p-4 bg-red-50 rounded-lg">
                <h3 className="text-md font-medium text-red-900 mb-2">계약 종료 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-red-700">종료일</label>
                    <p className="text-sm text-red-900">
                      {new Date(contract.termination_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700">종료 사유</label>
                    <p className="text-sm text-red-900">{contract.termination_reason}</p>
                  </div>
                  {contract.termination_notice_date && (
                    <div>
                      <label className="block text-sm font-medium text-red-700">해지 통보일</label>
                      <p className="text-sm text-red-900">
                        {new Date(contract.termination_notice_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RMCContractDetail;
